import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ReferencePersonFormContainerProps } from "models/propTypes/customerPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CustomerFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormikProps } from "formik";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import ReferencePersonForm from "./referencePersonForm";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { toReferencePersonPost } from "./referencePersonMapping";
import * as Yup from "yup";
import { UtilsHelper } from "utils/helpers";

const ReferencePersonFormContainer: ForwardRefRenderFunction<FormHandles, ReferencePersonFormContainerProps> = (
  props,
  ref
) => {
  const invoiceService = useInvoiceService();
  const companyEInvoicingEnabled = useSelector((store: AppState) => store.auth.selectedCompany.pageroEInvoice);

  const onSubmitForm = async (values: CustomerFormTypes.ReferencePerson) => {
    if (props.customerId) {
      const customerId = props.customerId;
      const referenceId = values.id;

      const referencePerson = toReferencePersonPost(customerId, values);

      const updatedReferenceId = await saveCustomerReference(referencePerson, referenceId);
      return props.handleSubmit({ ...values, id: updatedReferenceId });
    } else {
      return props.handleSubmit(values);
    }
  };

  const saveCustomerReference = async (referencePerson: WintSuperkollAPIModel.ReferencePost, referenceId?: number) => {
    return await (
      referenceId
        ? invoiceService.updateReferencePerson(referenceId, referencePerson)
        : invoiceService.createReferencePerson(referencePerson)
    ).then((res) => res.data);
  };

  const validationSchema = Yup.object<CustomerFormTypes.ReferencePerson>({
    customerId: Yup.number(),
    id: Yup.number(),
    name: Yup.string().required(TranslationKeys.Validations.Required),
    email: Yup.string(),
    emailSplit: Yup.array().of(Yup.string().email(TranslationKeys.Validations.InvalidEmail)),
    phoneNumber: Yup.string(),
    referenceId: Yup.string(),
    isDefault: Yup.boolean(),
    isEInvoicesEnabled: Yup.boolean(),
  });

  const defaultValues: CustomerFormTypes.ReferencePerson = {
    id: props.id,
    customerId: props.customerId ?? 0,
    name: props.name ?? "",
    email: props.email,
    emailSplit: props.email ? UtilsHelper.splitMultipleEmails(props.email) : [],
    phoneNumber: props.phoneNumber,
    referenceId: props.referenceId,
    isDefault: props.isDefault,
    isEInvoicesEnabled: companyEInvoicingEnabled,
  };

  const onEmailsUpdate = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<CustomerFormTypes.ReferencePerson>
  ) => {
    const { values, setFieldValue } = formikProps;

    setFieldValue(
      FormFieldConstants.Customer.ReferencePerson.Email,
      values.emailSplit?.filter((email) => !UtilsHelper.isEmpty(email)).join(",")
    );
  };

  return (
    <ReferencePersonForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={onSubmitForm}
      emailsUpdateHandler={onEmailsUpdate}
    />
  );
};

export default forwardRef(ReferencePersonFormContainer);
